import { useState } from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import { Briefcase, CreditCard, Globe2, Inbox, LayoutGrid, Loader2, LogOut, Menu, Package, Settings, ShieldAlert, Sparkles, Users } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";

const ITEMS = [
  { to: "/admin/leads", label: "Leads", icon: Inbox, id: "leads" },
  { to: "/admin/clients", label: "Clients", icon: Users, id: "clients" },
  { to: "/admin/projects", label: "Projects", icon: Briefcase, id: "projects" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, id: "payments" },
  { to: "/admin/packages", label: "Packages", icon: Package, id: "packages" },
  { to: "/admin/studio", label: "AI Studio", icon: Sparkles, id: "studio" },
  { to: "/admin/portfolio", label: "Portfolio", icon: LayoutGrid, id: "portfolio" },
  { to: "/admin/tracker", label: "Website Tracker", icon: Globe2, id: "tracker" },
  { to: "/admin/settings/20i", label: "Settings · 20i", icon: Settings, id: "settings-20i" },
  { to: "/admin/settings/studio-pricing", label: "Settings · AI Studio pricing", icon: Settings, id: "settings-studio-pricing" },
];

const SideNav = ({ items, onNavigate, prefix }) => (
  <nav className="flex flex-col gap-1">
    {items.map(({ to, label, icon: Icon, id, end }) => (
      <NavLink key={id} to={to} end={end} onClick={onNavigate} data-testid={`${prefix}-nav-${id}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-cyan-400/10 text-cyan-300 shadow-[inset_2px_0_0_#00F0FF]" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
        <Icon className="h-4 w-4" /> {label}
      </NavLink>
    ))}
  </nav>
);

const UserBox = ({ user, logout, prefix }) => (
  <div className="border-t border-white/5 pt-4">
    <p className="truncate text-sm font-medium text-white">{user.name || "Admin"}</p>
    <p className="truncate text-xs text-slate-500">{user.email}</p>
    <button onClick={logout} data-testid={`${prefix}-logout-button`}
      className="mt-3 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-red-300">
      <LogOut className="h-4 w-4" /> Log out
    </button>
  </div>
);

const Denied = ({ user, logout }) => (
  <div data-testid="admin-access-denied" className="flex min-h-screen items-center justify-center bg-[#07090E] px-4">
    <div className="max-w-md rounded-2xl border border-white/10 bg-[#0D111A] p-8 text-center">
      <ShieldAlert className="mx-auto h-8 w-8 text-amber-300" />
      <h1 className="mt-4 text-2xl font-bold text-white">Access restricted</h1>
      <p className="mt-2 text-sm text-slate-400">{user.email} doesn't have admin access.</p>
      <div className="mt-6 flex justify-center gap-3">
        <a href="/portal" data-testid="access-denied-portal-link" className="rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300">Go to my portal</a>
        <button onClick={logout} data-testid="access-denied-logout-button" className="rounded-full border border-white/15 px-5 py-2 text-sm text-slate-200 hover:border-cyan-400/50">Log out</button>
      </div>
    </div>
  </div>
);

export const DashShell = ({ items, user, logout, home, prefix }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#07090E]">
      <aside data-testid={`${prefix}-sidebar`} className="fixed inset-y-0 left-0 hidden w-64 flex-col justify-between border-r border-white/5 bg-[#0B0F18] p-5 lg:flex">
        <div className="space-y-8">
          <Logo to={home} testId={`${prefix}-logo`} />
          <SideNav items={items} prefix={prefix} />
        </div>
        <UserBox user={user} logout={logout} prefix={prefix} />
      </aside>
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-white/5 bg-[#07090E]/85 px-4 backdrop-blur-xl lg:hidden">
        <Logo to={home} testId={`${prefix}-mobile-logo`} />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid={`${prefix}-mobile-menu-toggle`} aria-label="Open menu" className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-slate-200"><Menu className="h-5 w-5" /></button>
          </SheetTrigger>
          <SheetContent side="left" className="flex w-72 flex-col justify-between border-white/10 bg-[#0B0F18]">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="mt-8"><SideNav items={items} prefix={`${prefix}-mobile`} onNavigate={() => setOpen(false)} /></div>
            <UserBox user={user} logout={logout} prefix={`${prefix}-mobile`} />
          </SheetContent>
        </Sheet>
      </header>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-10 lg:py-12"><Outlet /></div>
      </main>
    </div>
  );
};

export const FullLoader = () => (
  <div data-testid="dash-loading" className="grid min-h-screen place-items-center bg-[#07090E]"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [loggedOut, setLoggedOut] = useState(false);
  const doLogout = async () => { await logout(); setLoggedOut(true); };

  if (loggedOut) return <Navigate to="/login" replace />;
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Denied user={user} logout={doLogout} />;
  return <DashShell items={ITEMS} user={user} logout={doLogout} home="/admin/leads" prefix="admin" />;
}

export const AdminHeader = ({ eyebrow, title, children, action }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-cyan-400">{eyebrow}</p>
      <h1 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">{title}</h1>
      {children && <p className="mt-1 text-sm text-slate-400">{children}</p>}
    </div>
    {action}
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-dashed border-white/10 bg-[#0B0F18] px-6 py-16 text-center">
    <Icon className="mx-auto h-8 w-8 text-cyan-300/70" />
    <p className="mt-4 text-lg font-semibold text-white">{title}</p>
    {text && <p className="mt-1 text-sm text-slate-500">{text}</p>}
  </div>
);
